import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import type { Node, Space } from "@melo/shared";
import { DISCIPLINE_LABELS } from "@melo/shared/client";
import { store, useFeed, useTables } from "../lib/store";
import { api } from "../lib/api";
import { ago, descendantIds, formatDate, keyOf, statusOf, todayIso, viewsOf } from "../lib/select";
import { checklist } from "../lib/actions";
import { openItem, spaceHref } from "../lib/nav";
import { can, canEditNode, ROLE_LABEL } from "../lib/access";
import { useNow } from "../lib/useNow";
import { lower, milestoneWord, plural } from "../lib/words";
import { Avatar, Empty } from "../components/primitives";
import { StatusChip } from "../components/pickers";
import { Markdown } from "../components/Markdown";
import { describeEntry, eventNode, feedEntries, isWorkEvent } from "../components/activity";
import { Icon } from "../components/icons";
import { currentMilestone, milestonesOf } from "../components/FilterBar";

const DAY = 86_400_000;
const daysBetween = (a: string, b: string) => Math.round((new Date(`${b}T00:00:00`).getTime() - new Date(`${a}T00:00:00`).getTime()) / DAY);

/** "Ships Fri, Oct 2 · in 7 days", "Ships today", "3 days late": the countdown a producer lives by. */
export function shipLine(m: Node, done: boolean, word: string): { text: string; tone: "" | "late" | "soon" } {
  if (done) return { text: `Shipped${m.dueDate ? ` ${formatDate(m.dueDate)}` : ""}`, tone: "" };
  if (!m.dueDate) return { text: "No ship date", tone: "" };
  const d = daysBetween(todayIso(), m.dueDate);
  const when = new Date(`${m.dueDate}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const verb = word === "Update" ? "Ships" : "Due";
  if (d < 0) return { text: `${verb} ${when} · ${-d} ${-d === 1 ? "day" : "days"} late`, tone: "late" };
  if (d === 0) return { text: `${verb} today`, tone: "soon" };
  return { text: `${verb} ${when} · in ${d} ${d === 1 ? "day" : "days"}`, tone: d <= 2 ? "soon" : "" };
}

/** A project's home: what ships next and whether it will make it, then everything else coming up. */
export function Overview({ space, onNewMilestone, onPlan, onShip, onTeam }: { space: Space; onNewMilestone: () => void; onPlan: () => void; onShip: (m: Node) => void; onTeam: () => void }) {
  const t = useTables();
  const now = useNow(60_000);
  const today = todayIso();
  const word = milestoneWord(t, space.id);
  const [showAllDone, setShowAllDone] = useState(false);
  const nodes = useMemo(() => [...t.nodes.values()].filter((n) => n.spaceId === space.id && !n.draft), [t, space.id]);
  const kindKey = (n: Node) => t.kinds.get(n.kindId)?.key;
  const cat = (n: Node) => statusOf(t.kinds.get(n.kindId), n.status)?.category ?? "todo";
  const work = nodes.filter((n) => kindKey(n) !== "phase" && kindKey(n) !== "doc" && kindKey(n) !== "system");
  const producer = can(space.id, "producer");

  const milestones = milestonesOf(t, space.id);
  const current = currentMilestone(t, space.id);
  const shown = (showAllDone ? milestones : milestones.filter((m) => cat(m) !== "done")).filter((m) => m.id !== current?.id);
  const progress = (m: Node) => {
    const ids = descendantIds(t, m.id);
    const items = work.filter((n) => ids.has(n.id));
    return { items, total: items.length, done: items.filter((n) => cat(n) === "done").length, late: items.filter((n) => n.dueDate && n.dueDate < today && cat(n) !== "done").length };
  };

  const questions = work.filter((n) => n.blocker);
  const overdue = work.filter((n) => n.dueDate && n.dueDate < today && cat(n) !== "done").sort((a, b) => a.dueDate!.localeCompare(b.dueDate!));
  const review = work.filter((n) => cat(n) === "review");
  const members = [...t.members.values()].filter((m) => m.spaceId === space.id);
  const load = (actorId: string) => {
    const mine = work.filter((n) => cat(n) !== "done" && n.assignees.some((a) => a.actorId === actorId && a.role === "owner"));
    return { open: mine.length, late: mine.filter((n) => n.dueDate && n.dueDate < today).length };
  };
  useEffect(() => {
    // The boot catch-up only covers the latest events across every project, so load this one's history.
    void api.activity(space.id).then((r) => store.applyEvents(r.events), () => {});
  }, [space.id]);
  const feed = useFeed();
  const entries = useMemo(() => feedEntries(feed.filter((e) => e.spaceId === space.id && isWorkEvent(e)), 14), [feed, space.id]);

  const row = (n: Node, extra?: React.ReactNode) => (
    <button key={n.id} type="button" className="row" onClick={() => openItem(keyOf(t, n))}>
      <span className="mono muted key">{keyOf(t, n)}</span>
      <span className="t">
        <span>{n.title}</span>
        <small>{n.parentId ? t.nodes.get(n.parentId)?.title : `No ${lower(word)}`}</small>
      </span>
      {extra}
      {n.assignees[0] && <Avatar actor={t.actors.get(n.assignees[0].actorId)} size="sm" />}
    </button>
  );

  return (
    <div className="pad overview">
      <div className="ov-main">
        <div className="ov-head">
          <div>
            <h1>{space.name}</h1>
            <p className="ov-stage">{space.stage === "live" ? "Live game" : "In development"}</p>
            {space.description && <Markdown text={space.description} className="md ov-desc" />}
          </div>
        </div>

        {current ? (
          <NextUp space={space} m={current} word={word} producer={producer} progress={progress(current)} onShip={() => onShip(current)} />
        ) : (
          <section className="block next-up">
            <Empty title={`No ${lower(word)} is planned.`}>
              {space.stage === "live" ? "Plan the next few weekly or monthly updates, each with its ship checklist." : "Plan sprints toward your next milestone, or add a milestone."}
            </Empty>
            {producer && (
              <div className="next-actions">
                <button type="button" className="btn pri" onClick={onPlan}>
                  <Icon name="calendar" /> Plan {lower(plural(word))}
                </button>
                <button type="button" className="btn" onClick={onNewMilestone}>
                  <Icon name="plus" /> {word}
                </button>
              </div>
            )}
          </section>
        )}

        <section className="block" aria-labelledby="ms-h">
          <div className="block-h">
            <h3 id="ms-h">{current ? "Coming up" : plural(word)}</h3>
            <span className="muted">{shown.length || ""}</span>
            <span className="bar-r">
              {milestones.some((m) => cat(m) === "done") && (
                <button type="button" className="btn ghost sm" onClick={() => setShowAllDone((v) => !v)}>
                  {showAllDone ? "Hide shipped" : "Show shipped"}
                </button>
              )}
              {producer && milestones.length > 0 && (
                <button type="button" className="btn ghost sm" onClick={onPlan}>
                  <Icon name="calendar" /> Plan {lower(plural(word))}
                </button>
              )}
              {can(space.id, "member") && (
                <button type="button" className="btn sm" onClick={onNewMilestone}>
                  <Icon name="plus" /> {word}
                </button>
              )}
            </span>
          </div>
          {shown.map((m) => {
            const p = progress(m);
            const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
            const late = m.dueDate && m.dueDate < today && cat(m) !== "done";
            return (
              <button key={m.id} type="button" className="row ms-row" onClick={() => openItem(keyOf(t, m))}>
                <Icon name="flag" />
                <span className="t">
                  <span>{m.title}</span>
                  <small>
                    {m.startDate || m.dueDate ? `${formatDate(m.startDate)}${m.startDate && m.dueDate ? " – " : ""}${formatDate(m.dueDate)}` : "No dates"}
                    {p.total ? ` · ${p.done} of ${p.total} done` : " · no tasks yet"}
                    {p.late ? ` · ${p.late} late` : ""}
                  </small>
                </span>
                <span className="ms-meter" aria-label={`${pct}% done`}>
                  <span className="meter">
                    <i style={{ width: `${pct}%` }} />
                  </span>
                  <span className="mono">{pct}%</span>
                </span>
                {late ? <span className="chip err">Late</span> : <StatusChip kind={t.kinds.get(m.kindId)} status={m.status} />}
              </button>
            );
          })}
          {!shown.length && <Empty title={current ? `Nothing after ${current.title} yet.` : `No ${lower(plural(word))} yet.`}>{producer ? `Plan ${lower(plural(word))} to line up the next few at once.` : undefined}</Empty>}
        </section>

        <section className="block" aria-labelledby="att-h">
          <div className="block-h">
            <h3 id="att-h">Needs attention</h3>
            <span className="muted">{questions.length + overdue.length + review.length || ""}</span>
          </div>
          {questions.map((n) => row(n, <span className="chip sig">{n.blocker!.kind === "needs_input" ? "Question" : "Blocked"}</span>))}
          {overdue.slice(0, 8).map((n) => row(n, <span className="chip err">Due {formatDate(n.dueDate)}</span>))}
          {review.slice(0, 8).map((n) => row(n, <span className="chip agent">In review</span>))}
          {!questions.length && !overdue.length && !review.length && <Empty title="Nothing is late, blocked or waiting for review." />}
        </section>
      </div>

      <div className="ov-side">
        <section className="block" aria-labelledby="team-h">
          <div className="block-h">
            <h3 id="team-h">Team</h3>
            <span className="muted">{members.length || ""}</span>
            <button type="button" className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={onTeam}>
              {producer ? "Manage" : "View"}
            </button>
          </div>
          {members
            .slice()
            .sort((a, b) => ({ producer: 0, member: 1, contractor: 2 })[a.role] - ({ producer: 0, member: 1, contractor: 2 })[b.role])
            .map((m) => {
              const p = t.actors.get(m.actorId);
              if (!p) return null;
              const l = load(p.id);
              return (
                <div key={m.actorId} className="row">
                  <Avatar actor={p} size="sm" />
                  <span className="t">
                    <span>{p.name}</span>
                    <small>
                      {ROLE_LABEL[m.role]}
                      {p.disciplines.length ? ` · ${p.disciplines.map((d) => DISCIPLINE_LABELS[d] ?? d).join(", ")}` : ""}
                    </small>
                  </span>
                  <span className="num muted" title="Open tasks">
                    {l.open}
                  </span>
                  {l.late > 0 && <span className="chip err">{l.late} late</span>}
                </div>
              );
            })}
          {!members.length && <Empty title="No one is on this project yet." />}
        </section>

        <section className="block feed" aria-labelledby="act-h">
          <div className="block-h">
            <h3 id="act-h">Recent activity</h3>
          </div>
          {entries.map((entry) => {
            const e = entry.first;
            const n = eventNode(t, e);
            return (
              <button key={entry.key} type="button" className="fi" onClick={() => n && t.nodes.has(n.id) && openItem(keyOf(t, n))} disabled={!n || !t.nodes.has(n.id)}>
                <Avatar actor={t.actors.get(e.actorId)} size="sm" />
                <span className="grow">
                  <b>{t.actors.get(e.actorId)?.name ?? "Someone"}</b> {describeEntry(t, entry)}
                  {e.via !== "web" && <span className="via">{e.via}</span>}
                </span>
                <span className="mono muted">{ago(e.createdAt, now)}</span>
              </button>
            );
          })}
          {!entries.length && <Empty title="No activity yet." />}
        </section>
      </div>
    </div>
  );
}

/** The update or milestone in progress: when it ships, whether it will make it, and what is in the way. */
function NextUp({ space, m, word, producer, progress, onShip }: { space: Space; m: Node; word: string; producer: boolean; progress: { items: Node[]; total: number; done: number; late: number }; onShip: () => void }) {
  const t = useTables();
  const today = todayIso();
  const cat = (n: Node) => statusOf(t.kinds.get(n.kindId), n.status)?.category ?? "todo";
  const open = progress.items.filter((n) => cat(n) !== "done");
  const blocked = open.filter((n) => n.blocker).length;
  const unassigned = open.filter((n) => !n.assignees.some((a) => a.role === "owner")).length;
  const inReview = open.filter((n) => cat(n) === "review").length;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const line = shipLine(m, cat(m) === "done", word);
  const boards = viewsOf(t, space.id).filter((v) => v.type === "board");
  const board = boards.find((v) => !v.config.rootId && v.config.groupBy === "status") ?? boards.find((v) => !v.config.rootId) ?? boards[0];
  const boardHref = (extra = "") => (board ? `${spaceHref(space.slug, board.id)}?in=${m.id}${extra}` : spaceHref(space.slug));
  const byDiscipline = new Map<string, { total: number; done: number }>();
  for (const n of progress.items) {
    const d = typeof n.fields.discipline === "string" ? n.fields.discipline : null;
    if (!d) continue;
    const e = byDiscipline.get(d) ?? { total: 0, done: 0 };
    e.total++;
    if (cat(n) === "done") e.done++;
    byDiscipline.set(d, e);
  }
  const editable = canEditNode(m);
  const checks = m.checklist;
  return (
    <section className="block next-up" aria-label={`Now: ${m.title}`}>
      <div className="next-h">
        <span className="next-kicker">Now</span>
        <span className={`next-when ${line.tone}`}>{line.text}</span>
      </div>
      <div className="next-title">
        <button type="button" className="next-name" onClick={() => openItem(keyOf(t, m))}>
          {m.title}
        </button>
        <span className="grow" />
        <Link className="btn sm" href={boardHref()}>
          <Icon name="board" /> Open board
        </Link>
        {producer && (
          <button type="button" className="btn pri sm" onClick={onShip}>
            Ship {lower(word)}
          </button>
        )}
      </div>
      <div className="next-progress" aria-label={`${pct}% done`}>
        <div className="meter big">
          <i style={{ width: `${pct}%` }} />
        </div>
        <span>
          <b>{progress.done}</b> of {progress.total} done
        </span>
      </div>
      <div className="next-stats">
        <Stat label="Late" n={progress.late} tone="err" href={boardHref("&due=overdue")} />
        <Stat label="Blocked" n={blocked} tone="sig" />
        <Stat label="No owner" n={unassigned} tone="warn" href={boardHref("&who=none")} />
        <Stat label="In review" n={inReview} tone="ok" href={boardHref("&status=cat:review")} />
      </div>
      {byDiscipline.size > 0 && (
        <div className="next-disc" aria-label="By discipline">
          {[...byDiscipline.entries()]
            .sort((a, b) => b[1].total - a[1].total)
            .map(([d, e]) => (
              <Link key={d} className="disc-row" href={boardHref(`&does=${d}`)}>
                <span className="disc-l">{DISCIPLINE_LABELS[d] ?? d}</span>
                <span className="meter">
                  <i style={{ width: `${(e.done / e.total) * 100}%` }} />
                </span>
                <span className="mono muted">
                  {e.done}/{e.total}
                </span>
              </Link>
            ))}
        </div>
      )}
      {checks.length > 0 && (
        <div className="next-checks" role="group" aria-label="Ship checklist">
          <div className="next-checks-h">
            <span>Ship checklist</span>
            <span className="mono muted">
              {checks.filter((c) => c.done).length}/{checks.length}
            </span>
          </div>
          <div className="next-checks-b">
            {checks.map((c) => (
              <label key={c.id} className={`next-check${c.done ? " done" : ""}`}>
                <input type="checkbox" checked={c.done} disabled={!editable} onChange={() => void checklist(m, { op: "set", nodeId: m.id, itemId: c.id, done: !c.done })} />
                {c.text}
              </label>
            ))}
          </div>
        </div>
      )}
      {m.dueDate && daysBetween(today, m.dueDate) < 0 && open.length > 0 && producer && (
        <p className="hint">
          This {lower(word)} is past its date with {open.length} open. Ship it and carry the rest forward, or move the date.
        </p>
      )}
    </section>
  );
}

function Stat({ label, n, tone, href }: { label: string; n: number; tone: string; href?: string }) {
  const body = (
    <>
      <b className={n ? tone : ""}>{n}</b>
      <span>{label}</span>
    </>
  );
  return href && n ? (
    <Link className="stat" href={href}>
      {body}
    </Link>
  ) : (
    <span className="stat">{body}</span>
  );
}
